var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { createElement as _createElement } from "react";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef } from 'react';
import { MapContainer, TileLayer, ImageOverlay, Marker, Rectangle, Polygon, Circle, Tooltip, Popup, useMap } from 'react-leaflet';
import { CRS, divIcon } from 'leaflet';
// A CSS-only default marker icon — no image dependency, works offline.
// Styled via .uxpcore_map__default-marker in _map.scss.
const defaultMarkerIcon = divIcon({
    className: 'uxpcore_map__default-marker',
    iconSize: [20, 30],
    iconAnchor: [10, 30],
    popupAnchor: [0, -32],
});
import HeatmapLayer from './HeatmapLayer';
import { useUXPContext } from '@core/context/useUXPContext';
import { ThemeType } from '@theme/themeUtils';
function getStaticImageBounds(w, h) {
    return [[0, 0], [h, w]];
}
function convertFromImagePosition(x, y, _w, h) {
    return [h - y, x];
}
const ExtendedMarker = (props) => {
    const { showPopupOnLoad, children } = props, markerProps = __rest(props, ["showPopupOnLoad", "children"]);
    const markerRef = useRef(null);
    useEffect(() => {
        if (showPopupOnLoad && markerRef.current) {
            // Use setTimeout to ensure the marker is fully rendered
            setTimeout(() => {
                if (markerRef.current) {
                    markerRef.current.openPopup();
                }
            }, 100);
        }
    }, [showPopupOnLoad]);
    return (_jsx(Marker, Object.assign({}, markerProps, { ref: markerRef, children: children })));
};
const MarkerLayer = ({ markers, onMarkerClick, staticImage }) => {
    const renderMarker = (marker, key) => {
        var _a;
        let additionalProps = {};
        if (marker.customHTMLIcon) {
            additionalProps.icon = divIcon(marker.customHTMLIcon);
        }
        else {
            additionalProps.icon = defaultMarkerIcon;
        }
        let { latitude, longitude } = marker;
        if (marker.imageCoordinates && staticImage) {
            [latitude, longitude] = convertFromImagePosition(longitude, latitude, staticImage.width, staticImage.height);
        }
        return (_jsxs(ExtendedMarker, Object.assign({ position: [latitude, longitude], showPopupOnLoad: (_a = marker.renderPopup) === null || _a === void 0 ? void 0 : _a.showOnLoad, draggable: marker.draggable, eventHandlers: Object.assign(Object.assign(Object.assign({}, ((marker === null || marker === void 0 ? void 0 : marker.onclick) || onMarkerClick ? {
                click: (e) => {
                    if (marker === null || marker === void 0 ? void 0 : marker.onclick) {
                        marker.onclick(e);
                    }
                    if (onMarkerClick) {
                        onMarkerClick(e, marker.data);
                    }
                }
            } : {})), ((marker === null || marker === void 0 ? void 0 : marker.ondragstart) ? { dragstart: marker.ondragstart } : {})), ((marker === null || marker === void 0 ? void 0 : marker.ondragend) ? { dragend: marker.ondragend } : {})) }, additionalProps, { children: [marker.renderPopup && _jsxs(Popup, { children: [" ", marker.renderPopup.content(), " "] }), marker.renderTooltip &&
                    _jsx(Tooltip, { direction: marker.renderTooltip.direction || 'auto', permanent: marker.renderTooltip.keepShowing || false, children: marker.renderTooltip.content() })] }), key));
    };
    return (_jsx(_Fragment, { children: markers.map((marker, key) => renderMarker(marker, key)) }));
};
const RegionLayer = ({ regions, onRegionClick, staticImage }) => {
    const renderRegion = (region, key) => {
        // get props
        let regionProps = {};
        if (region.hideStroke) {
            regionProps.stroke = !region.hideStroke;
        }
        if (region.color) {
            regionProps.color = region.color;
        }
        if (region.fillColor) {
            regionProps.fillColor = region.fillColor;
        }
        // region click handler
        const handleRegionClick = (event) => {
            if (onRegionClick) {
                onRegionClick(event, region.data);
            }
        };
        if (region.type) {
            if (region.type == "circle") {
                let bound = region.bounds;
                if (bound.hasOwnProperty("center") && bound.hasOwnProperty("radius")) {
                    let _b = bound;
                    return _createElement(Circle, Object.assign({ center: _b.center, radius: _b.radius }, regionProps, { key: key }, (onRegionClick ? {
                        eventHandlers: {
                            click: handleRegionClick
                        }
                    } : {})));
                }
            }
            if (region.type == "rectangle") {
                let _b = region.bounds;
                return _createElement(Rectangle, Object.assign({ bounds: _b }, regionProps, { key: key }, (onRegionClick ? {
                    eventHandlers: {
                        click: handleRegionClick
                    }
                } : {})));
            }
        }
        let _b = region.bounds;
        if (region.imageCoordinates && staticImage) {
            _b = _b.map(x => convertFromImagePosition(x[0], x[1], staticImage.width, staticImage.height));
        }
        return _createElement(Polygon, Object.assign({ positions: _b }, regionProps, { key: key }, (onRegionClick ? {
            eventHandlers: {
                click: handleRegionClick
            }
        } : {})), region.tooltipContent && _jsx(Tooltip, { children: region.tooltipContent(region.data) }));
    };
    if (!regions || regions.length === 0) {
        return null;
    }
    return (_jsx(_Fragment, { children: regions.map((region, key) => renderRegion(region, key)) }));
};
const MapEventHandler = ({ onClick, onZoomEnd, onDragEnd }) => {
    const map = useMap();
    useEffect(() => {
        if (onClick) {
            map.on('click', onClick);
        }
        if (onZoomEnd) {
            map.on('zoomend', onZoomEnd);
        }
        if (onDragEnd) {
            map.on('dragend', onDragEnd);
        }
        return () => {
            if (onClick) {
                map.off('click', onClick);
            }
            if (onZoomEnd) {
                map.off('zoomend', onZoomEnd);
            }
            if (onDragEnd) {
                map.off('dragend', onDragEnd);
            }
        };
    }, [map, onClick, onZoomEnd, onDragEnd]);
    return null;
};
/**
 * Helper function to get default map URL based on theme
 */
function getDefaultMapUrl(themeType, themeName) {
    // Light themes get light tiles
    if (themeType === ThemeType.Light || themeName === 'Glass Light') {
        return "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
    }
    // Dark/Glass Dark themes get dark tiles
    return "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png";
}
/**
 * A map widget that can show a pannable/zoomable map with markers
 * @export
 *
 */
const MapComponent = (props) => {
    const context = useUXPContext();
    // props
    let { mapUrl: providedMapUrl, markers = [], onMarkerClick, center, zoom = 5, maxZoom = 19, minZoom = -19, regions, onClick, onRegionClick, staticImage, zoomOnScroll = false, onDragEnd, onZoomEnd, heatmap, hideZoomControlls } = props;
    // Use provided mapUrl or get default based on theme
    const mapUrl = providedMapUrl || getDefaultMapUrl(context.themeType, context.themeName);
    // get center
    let _center = [1.290270, 103.851959];
    let _renderCenter = false;
    if (center) {
        _center = [center.position.latitude, center.position.longitude];
        if (center.renderMarker)
            _renderCenter = center.renderMarker;
    }
    else {
        if (markers.length > 0) {
            _center = [markers[0].latitude, markers[0].longitude];
        }
    }
    // Prepare heatmap coordinate converter for static images
    const heatmapCoordinateConverter = (heatmap === null || heatmap === void 0 ? void 0 : heatmap.imageCoordinates) && staticImage
        ? (lat, lng) => convertFromImagePosition(lng, lat, staticImage.width, staticImage.height)
        : undefined;
    return (_jsxs(MapContainer, { id: "uxp-map-component-container", className: "uxpcore_mapcontainer", crs: props.staticImage ? CRS.Simple : CRS.EPSG3857, attributionControl: false, center: _center, zoom: zoom, maxZoom: maxZoom, minZoom: minZoom, scrollWheelZoom: zoomOnScroll, zoomControl: !hideZoomControlls, children: [_jsx(MapEventHandler, { onClick: onClick, onZoomEnd: onZoomEnd, onDragEnd: onDragEnd }), mapUrl && _jsx(TileLayer, { url: mapUrl, noWrap: true }), staticImage &&
                _jsx(ImageOverlay, { url: staticImage.url, bounds: staticImage.bounds ? staticImage.bounds : getStaticImageBounds(staticImage.width, staticImage.height) }), _jsx(MarkerLayer, { markers: markers, onMarkerClick: onMarkerClick, staticImage: staticImage }), _jsx(RegionLayer, { regions: regions, onRegionClick: onRegionClick, staticImage: staticImage }), _renderCenter && _jsx(Marker, { position: _center }), heatmap && heatmap.values && heatmap.values.length > 0 && (_jsx(HeatmapLayer, { points: heatmap.values, radius: heatmap.radius, blur: heatmap.blur, max: heatmap.max, gradient: heatmap.gradient, coordinateConverter: heatmapCoordinateConverter }))] }));
};
export default MapComponent;
